/**
 * The dock (phone) and the menu sheet.
 *
 * Listens for:
 *   sunday:buy    (from the page's buy box)  keep the dock's price and size label in step; the dock's Add
 *                                            follows the page's choice in the cart (@sunday/incart).
 *   sunday:adding (from an Add, before the network) the button says "Adding…" and the cart badge bumps.
 *   sunday:added  a mattress or furniture: the cart opens. An accessory (detail.stay): the page stays and
 *                 the button turns into "− n in cart +". No pop-up (AR, 17 Oct 2026).
 *   sunday:add-failed  the button comes back; the page's own error line says why.
 * The cart is the only basket: everything here reads the cart that @sunday/cart returns.
 */
import * as cart from '@sunday/cart';
import { t as T } from '@sunday/text';
import { paint as paintIn } from '@sunday/incart';

/* Marks the menu sheet (narrow on desktop); an internal label, never shown. */
const MENU = 'menu';

const $ = (s, r = document) => r.querySelector(s);

const ov = document.getElementById('ov');
const sheet = ov && $('.sd-bsheet', ov);
const body = ov && $('[data-sheet-body]', ov);
const head = ov && $('[data-sheet-h]', ov);
let opener = null;
let opens = 0;
/* 31 Oct (speed, INP): run after the next paint (a frame, then a task) */
const afterPaint = (fn) => requestAnimationFrame(() => setTimeout(fn, 0));

function open(title, html, label, from) {
  if (!ov || !head || !body || !sheet) return;
  if (ov.hidden) opener = from || document.activeElement;
  head.textContent = title;
  sheet.setAttribute('aria-label', label || title);
  /* 3 Nov (AR go): the menu's language switch sits on the heading line; any other sheet has none there */
  const lh = $('.sd-shh', sheet);
  const oldLang = lh && $('.sd-mlrow', lh);
  if (oldLang) oldLang.remove();
  body.innerHTML = html;
  const lf = label === MENU && lh && $('.sd-mlrow', body);
  if (lf) lh.append(lf);
  ov.classList.toggle('sd-wide', matchMedia('(min-width: 720px)').matches && label !== MENU);
  ov.hidden = false;
  /* 31 Oct (speed, INP): the sheet paints first; the page's scroll lock and the focus follow right after that paint
     (focus() makes the browser bring the styles up to date inside the tap). Closed meanwhile: nothing happens. */
  const my = ++opens;
  afterPaint(() => {
    if (ov.hidden || my !== opens) return;
    document.documentElement.style.overflow = 'hidden';
    sheet.focus({ preventScroll: true });
  });
}

function close() {
  if (!ov || ov.hidden) return;
  opens++;
  ov.hidden = true;
  document.documentElement.style.overflow = '';
  if (opener && opener.focus && opener.isConnected) opener.focus({ preventScroll: true });
}

if (ov) {
  ov.addEventListener('click', (e) => {
    if (e.target.closest('[data-close]')) close();
  });
  document.addEventListener('keydown', (e) => {
    if (ov.hidden) return;
    if (e.key === 'Escape') close();
    if (e.key === 'Tab') {
      const f = [...sheet.querySelectorAll('a[href], button:not([disabled]), input, select')];
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) (e.preventDefault(), last.focus());
      else if (!e.shiftKey && document.activeElement === last) (e.preventDefault(), first.focus());
    }
  });
}

/* ---------- menu ---------- */
document.addEventListener('click', (e) => {
  if (!e.target.closest('[data-open-menu]')) return;
  const t = $('[data-menu-template]');
  /* 31 Oct (speed): the template's markup is read once (it never changes after the page loads) */
  if (t) open(T('sheet.menu'), t.__sdHtml || (t.__sdHtml = t.innerHTML), MENU);
  pickTag();
});

/* 30 Oct (AR go): "Your pick" on the picked mattress card (the record's own tag on the others; go 2: that card is
   the outlined one, else Most chosen), and one
   GA event per menu tap, menu_tap { item }. */
function pickTag() {
  let pk = null;
  try { pk = JSON.parse(localStorage.getItem('sunday-pick') || 'null'); } catch (e) { /* no pick */ }
  document.querySelectorAll('[data-sd-mxc]').forEach((row) => {
    row.querySelectorAll('[data-handle]').forEach((a) => {
      const mine = pk && pk.id === a.dataset.handle;
      a.toggleAttribute('data-hi', !!mine); /* 31 Oct (AR go 4): outlined only when it is the visitor's pick */
      const tag = a.querySelector('.sd-mm3-tag');
      if (!tag) return;
      tag.textContent = mine ? row.dataset.wPick : tag.dataset.eb;
      tag.classList.toggle('sd-mm3-pick', !!mine);
      tag.hidden = !tag.textContent;
    });
  });
}
document.addEventListener('sunday:pick', pickTag);
document.addEventListener('sunday:menu-cards', pickTag);
document.addEventListener('click', (e) => {
  const a = e.target.closest('[data-sd-menu]');
  if (!a || !e.target.closest('[data-sheet], [data-sd-npanel]')) return;
  try { if (typeof window.gtag === 'function') window.gtag('event', 'menu_tap', { item: a.dataset.sdMenu }); } catch (err) { /* the tap goes on */ }
});

/* ---------- dock ---------- */
const dock = $('[data-dock]');
function paintDock(d) {
  if (!dock || !d) return;
  const p = $('[data-dock-price]', dock);
  const l = $('[data-dock-label]', dock);
  if (p) p.textContent = d.price || '';
  if (l) l.textContent = d.label || '';
  const b = $('[data-dock-add]', dock);
  if (b) {
    b.disabled = !d.ready;
    if (d.variant) {
      b.dataset.sdV = d.variant;
      b.dataset.sdKind = d.kind || 'a';
      paintIn(b);
    }
  }
}
document.addEventListener('sunday:buy', (e) => paintDock(e.detail));
paintDock(window.sundayBuyLast);
document.addEventListener('click', (e) => {
  if (!e.target.closest('[data-dock-add]')) return;
  const add = document.querySelector('sunday-buy [data-add]');
  if (add) add.click();
});

/* ---------- after an Add (AR, 17 Oct): no pop-up ----------
   A mattress (or furniture) goes straight to the cart. An accessory stays on the page and its button shows
   "− n in cart +" (@sunday/incart). The header count bumps at once; sunday:cart then sets the true number. */

/* The Add button itself: "Adding…" at once, then its own label again (or the cart opens). */
function btnState(b, st) {
  if (!b || !b.isConnected) return;
  if (st === 'adding') {
    if (b.__sdLabel == null) {
      b.__sdLabel = b.innerHTML;
      b.style.minWidth = b.offsetWidth + 'px';
    }
    b.textContent = T('sheet.adding') || b.textContent;
    b.classList.add('sd-adding');
    b.setAttribute('aria-busy', 'true');
    return;
  }
  if (b.__sdLabel != null) b.innerHTML = b.__sdLabel;
  b.__sdLabel = null;
  b.style.minWidth = '';
  b.classList.remove('sd-adding', 'sd-addok');
  b.removeAttribute('aria-busy');
}

function bump(n) {
  const a = $('[data-cart-link]');
  if (!a) return;
  let em = $('[data-cart-count]', a);
  if (!em) {
    em = document.createElement('em');
    em.dataset.cartCount = '';
    a.append(em);
  }
  em.textContent = String((parseInt(em.textContent, 10) || 0) + n);
  em.classList.remove('sd-bump');
  void em.offsetWidth;
  em.classList.add('sd-bump');
}

document.addEventListener('sunday:adding', (e) => {
  const d = e.detail || {};
  btnState(d.btn, 'adding');
  bump(Math.max(1, Number(d.qty) || 1));
});

document.addEventListener('sunday:add-failed', (e) => {
  const d = e.detail || {};
  btnState(d.btn, 'rest');
  cart.get().then((c) => document.dispatchEvent(new CustomEvent('sunday:cart', { detail: { cart: c } }))).catch(() => {});
});

document.addEventListener('sunday:added', (e) => {
  const d = e.detail || {};
  if (d.stay) {
    btnState(d.btn, 'rest');
    paintIn(d.btn);
    return;
  }
  location.assign(window.Sunday?.cartUrl || '/cart');
});

/* Back from the cart (the browser's page cache): no button left saying "Adding…". */
window.addEventListener('pageshow', (e) => {
  if (e.persisted) document.querySelectorAll('.sd-adding').forEach((b) => btnState(b, 'rest'));
});

/* While someone types (pincode, custom size), the dock steps aside so it never covers a field or its button. */
if (dock) {
  document.addEventListener('focusin', (e) => {
    if (e.target.matches('input, textarea, select')) dock.hidden = true;
  });
  document.addEventListener('focusout', (e) => {
    if (e.target.matches('input, textarea, select')) setTimeout(() => (dock.hidden = !!document.activeElement?.matches('input, textarea, select')), 0);
  });
}

/* One look for the dock on every page (AR, 15 Oct): the same three places and the same colour all the way down.
   The main button is navy; only Add to cart and Checkout are marigold, as the page's one action.
   Nothing changes while the page scrolls (no colour swaps, no stepping aside). */
if (dock) {
  const main = $('[data-dock-main]', dock);
  if (main) main.classList.toggle('sd-nv', !main.matches('[data-dock-add], [name="checkout"]'));
}

/* 24 Oct (AR): the dock's Call shows only while the store is open (store facts hours, e.g. "10 am to 7 pm",
   India time), so a tap always reaches a person; WhatsApp stays. Checked again every minute. */
{
  const call = document.querySelector('.sd-dock .sd-dcall[data-hours]');
  const at = (t) => {
    const m = /(\d{1,2})(?::(\d{2}))?\s*(am|pm)/i.exec(t || '');
    if (!m) return null;
    return (Number(m[1]) % 12 + (/pm/i.test(m[3]) ? 12 : 0)) * 60 + Number(m[2] || 0);
  };
  if (call) {
    const [a, b] = String(call.dataset.hours).split(/\s+(?:to|–|-)\s+/i).map(at);
    const tick = () => {
      const d = new Date();
      const ist = (d.getUTCHours() * 60 + d.getUTCMinutes() + 330) % 1440;
      call.hidden = !(a != null && b != null && ist >= a && ist < b);
    };
    tick();
    setInterval(tick, 60000);
  }
}
